import {
    ChallengesPlaylist, DEFAULT_PLAYLIST_ID
} from '../../../components/challenges-ui-filters/playlist-filter/playlist-filter.types';
import { CourseData } from '@containers/activity-router/data-grid-container/courses/types/courses.types';
import {
    manageCourseTopicExpandCollapse
} from '@containers/activity-router/routes/helpers/courses/course-expand-helpers';
import { LevelFilter } from '@containers/activity-router/routes/learn/challenges/challenges-constants';
import {
    ChallengesDokFilter
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/dok-filter/dok-filter.types';
import {
    ChallengesFilterLevel, ChallengesLevel
} from '@containers/activity-router/routes/learn/challenges/components/challenges-ui-filters/level-filter/level-filter.types';
import {
    ChallengesComplexity,
    TransformCourse
} from '@services/gateways/combined-assign-gateway/combined-assign-gateway.types';

type GetChallengesFilteredCourseProps = {
    course: CourseData
    filterLevel: ChallengesFilterLevel
    filterDok: ChallengesDokFilter
    filterPlaylist: ChallengesPlaylist
    filterStrand?: Array<string>
    filterTopic?: Array<string>
};

/**
 * As per previous Challenges implementation Level 2 IS Level 3 and Level 11 is Level 10 from a data perspective.
 * This function clears that up for us and can be found in previous module.
 */
export const getUpdatedChallengesLevelFilter = (levelFilter: ChallengesFilterLevel): ChallengesFilterLevel => {
    const { MIN_LEVEL, MAX_LEVEL } = LevelFilter;
    const updated = levelFilter.map(level => {
        if (level === MIN_LEVEL) return level + 1;
        if (level === MAX_LEVEL) return level - 1;

        return level;
    });

    return updated;
};

export const getChallengesFilteredCourse = ({
    course,
    filterLevel,
    filterDok,
    filterPlaylist,
    filterStrand,
    filterTopic
}: GetChallengesFilteredCourseProps) => {
    if (!course) return { updatedCourse: course };

    // Check that this logic is catered for at yearGroup Initialisation or on click
    const updatedLevelFilter = getUpdatedChallengesLevelFilter(filterLevel);
    const isPlaylistOverride = filterPlaylist.id !== DEFAULT_PLAYLIST_ID;
    const playlistSet = isPlaylistOverride ? new Set(filterPlaylist.activityIds) : null;
    const filterLevelSet = new Set(updatedLevelFilter);
    const filterDokSet = new Set(filterDok);
    const filterStrandSet = new Set(filterStrand);
    const filterTopicSet = new Set(filterTopic);
    const hasStrandFilter = filterStrandSet.size > 0;
    const hasTopicFilter = filterTopicSet.size > 0;
    const hasStrandTopicFilter = hasStrandFilter || hasTopicFilter;
    const passLevel = (yearGroup: ChallengesLevel) =>
        filterLevelSet.size === 0 || (yearGroup != null && filterLevelSet.has(yearGroup));
    const passDok = (complexity: ChallengesComplexity) =>
        filterDokSet.size === 0 || (complexity != null && filterDokSet.has(complexity));
    const filteredTopics = course.topics?.flatMap((strand) => {
        const topics = strand.topics?.flatMap((topic) => {
            const isStrandSelected = filterStrandSet.has(strand.id);
            const isTopicSelected = filterTopicSet.has(topic.id);
            const passStrandTopic =
                !hasStrandTopicFilter ||
                isStrandSelected ||
                isTopicSelected;

            if (!passStrandTopic) return [];

            const activities = topic.activities.filter((activity) => {
                if (isPlaylistOverride) return playlistSet?.has(activity.id) ?? false;

                return (
                    passLevel(activity.extraProperties?.yearGroup) &&
                    passDok(activity.complexity as ChallengesComplexity)
                );
            });

            return activities.length ? [{
                ...topic,
                activities
            }] : [];
        });

        return topics?.length ? [{
            ...strand,
            topics
        }] : [];
    });
    const updatedCourse: TransformCourse = manageCourseTopicExpandCollapse({
        course: {
            ...course,
            topics: filteredTopics
        },
        isExpanded: true
    });

    return { updatedCourse };
};

// Level and playlist decide which challenges can stay selected. Topic and DOK only hide them, so a
// selection hidden by either is kept until it falls outside the level or playlist.
export const getChallengesSelectableCourse = ({
    course,
    filterLevel,
    filterPlaylist
}: Pick<GetChallengesFilteredCourseProps, 'course' | 'filterLevel' | 'filterPlaylist'>) => getChallengesFilteredCourse({
    course,
    filterLevel,
    filterDok: [],
    filterPlaylist
});
